import { useEffect, useRef } from 'react';
import { Hash, KeyRound, Wrench } from 'lucide-react';
import type { Suggestion } from '@/hooks/useFilterAutocomplete';

/**
 * Popup rendered below the WHERE / ORDER BY input. Keyboard-first —
 * navigation handled by the parent so Enter on the input still submits
 * the filter when no suggestion is selected (or `Tab` selects).
 *
 * Visual: small floating panel, max 8 rows, monospace column names with
 * a type badge on the right. Same yellow highlight on the matched
 * substring as the sidebar / command bar.
 */

const MAX_VISIBLE = 8;
const ROW_HEIGHT = 28;

interface Props {
  suggestions: Suggestion[];
  selectedIndex: number;
  /** The current partial token under the cursor. Highlights matching chars. */
  filter: string;
  /** Anchor element — popup positions itself just below this rect. */
  anchor: HTMLElement | null;
  onSelect: (index: number) => void;
  onHover: (index: number) => void;
}

export function FilterAutocompletePopup({
  suggestions,
  selectedIndex,
  filter,
  anchor,
  onSelect,
  onHover,
}: Props) {
  const listRef = useRef<HTMLDivElement>(null);

  // Keep the focused row in view while arrow-navigating.
  useEffect(() => {
    if (!listRef.current) return;
    const row = listRef.current.children[selectedIndex] as HTMLElement | undefined;
    row?.scrollIntoView({ block: 'nearest' });
  }, [selectedIndex]);

  if (suggestions.length === 0 || !anchor) return null;

  const rect = anchor.getBoundingClientRect();
  const popupStyle: React.CSSProperties = {
    position: 'fixed',
    top: rect.bottom + 2,
    left: rect.left,
    width: Math.max(rect.width, 240),
    maxHeight: MAX_VISIBLE * ROW_HEIGHT,
  };

  return (
    <div
      style={popupStyle}
      className="z-50 bg-dbgo-modal border rounded-sm shadow-2xl overflow-y-auto"
      // Don't steal focus from the input on click — apply the suggestion
      // via mousedown so the input keeps focus and the next keystroke
      // continues the typing flow.
      onMouseDown={(e) => e.preventDefault()}
    >
      <div ref={listRef}>
        {suggestions.map((s, i) => {
          const isSelected = i === selectedIndex;
          const Icon = s.kind === 'column'
            ? (s.typeBadge?.toLowerCase().includes('id') ? KeyRound : Hash)
            : Wrench;
          return (
            <button
              key={`${s.kind}:${s.text}:${i}`}
              type="button"
              className={`w-full flex items-center gap-2 px-2 h-[${ROW_HEIGHT}px] text-left text-xs transition-colors ${
                isSelected ? 'bg-dbgo-tab-hover ring-1 ring-accent-blue/40' : 'hover:bg-dbgo-tab-hover'
              }`}
              style={{ height: ROW_HEIGHT }}
              onMouseDown={(e) => { e.preventDefault(); onSelect(i); }}
              onMouseEnter={() => onHover(i)}
            >
              <Icon
                size={12}
                className={`flex-shrink-0 ${
                  s.kind === 'column' ? 'text-accent-blue' : 'text-text-muted'
                }`}
              />
              <span className="flex-1 font-mono text-text-primary truncate">
                <Highlighted text={s.display} term={filter} />
              </span>
              {s.typeBadge && (
                <span className="text-2xs font-mono text-text-muted bg-dbgo-badge px-1.5 py-[1px] rounded-sm flex-shrink-0">
                  {s.typeBadge}
                </span>
              )}
              {s.kind === 'operator' && (
                <span className="text-2xs uppercase text-text-muted">op</span>
              )}
              {s.kind === 'keyword' && (
                <span className="text-2xs uppercase text-text-muted">kw</span>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function Highlighted({ text, term }: { text: string; term: string }) {
  if (!term) return <>{text}</>;
  const lower = text.toLowerCase();
  const needle = term.toLowerCase();
  const idx = lower.indexOf(needle);
  if (idx === -1) return <>{text}</>;
  return (
    <>
      {text.slice(0, idx)}
      <mark className="bg-accent-yellow/30 text-text-primary rounded-sm px-[1px]">
        {text.slice(idx, idx + needle.length)}
      </mark>
      {text.slice(idx + needle.length)}
    </>
  );
}
